// components/PayrollAuthModal.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Col, Row } from '../lib/utils';
import { BoldText, DemiLightText, RegularText } from '../lib/Text';
import SvgIcon from './SvgIcon';
import { colors } from '../lib/colors';

type Props = {
    step: 'set' | 'confirm';
    /** 사용자가 “확인” 눌렀을 때 호출, 파라미터는 입력된 비밀번호(숫자 문자열) */
    onSubmit: (password: string) => void;
    /** 사용자가 “취소” 눌렀을 때 호출 */
    onCancel: () => void;
};

const PayrollAuthModal: React.FC<Props> = ({ step, onSubmit, onCancel }) => {
    const [pw1, setPw1] = useState('');
    const [pw2, setPw2] = useState('');
    const pw1Ref = useRef<HTMLInputElement>(null); // 👈 추가

    // 자동 포커스
    const inputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        inputRef.current?.focus();
    }, [step]);

    const handleOk = () => {
        if (step === 'set') {
            if (pw1 !== pw2) {
                alert('비밀번호가 일치하지 않습니다.');
                return;
            }
            onSubmit(pw1);
        } else {
            onSubmit(pw1);
        }
    };

    useEffect(() => {
        if (step === 'confirm') {
            pw1Ref.current?.focus(); // 👈 포커스
        }
    }, [step]);

    // 엔터시 확인
    const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            onSubmit(pw1);
        }
    };

    return (
        <Col className="w-full h-full  justify-center items-center  bg-[#F4F5F7]">
            <Col
                className="mt-[160px] w-[495px] self-center bg-white rounded-[15px] py-[36px] px-[32px]"
                style={{ boxShadow: '0px 0px 30px rgba(0,0,0,0.15)' }}
            >
                <BoldText fontSize={24} lineHeight={38} color="#3D4244" className="mb-2">
                    {step === 'set' ? '비밀번호를 설정해주세요' : '비밀번호를 입력해주세요'}
                </BoldText>
                <DemiLightText fontSize={16} lineHeight={23} color="#3D4244" className="mb-8">
                    2차 비밀번호 입력이 필요해요
                </DemiLightText>

                {/* 설정 스텝 */}
                {step === 'set' && (
                    <>
                        <Row className="mb-[12px]">
                            <DemiLightText className="mr-1">비밀번호 *</DemiLightText>
                            <DemiLightText>(숫자 4자~20자)</DemiLightText>
                        </Row>
                        <TextInput
                            // ref={inputRef}
                            value={pw1}
                            onChange={(v) => setPw1(String(v))}
                            placeholder="비밀번호를 입력해주세요"
                            height={68}
                            className="mb-[20px]"
                        />
                        <Row className="mb-[12px]">
                            <DemiLightText className="mr-1">비밀번호 확인 *</DemiLightText>
                        </Row>
                        <TextInput
                            value={pw2}
                            onChange={(v) => setPw2(String(v))}
                            placeholder="비밀번호 확인을 위해 다시 입력해주세요"
                            height={68}
                            className="mb-[30px]"
                        />
                    </>
                )}

                {/* 확인 스텝 */}
                {step === 'confirm' && (
                    <>
                        <TextInput
                            inputRef={pw1Ref} // 👈 여기 전달
                            value={pw1}
                            onChange={(v) => setPw1(String(v))}
                            placeholder="비밀번호를 입력해주세요"
                            height={68}
                            className="mb-[12px]"
                            onKeyDown={onEnter}
                        />
                        <Row className="items-center mb-[6px]">
                            <SvgIcon name="SvgLock" size={13} fill={colors.LAVEL_4} stroke={colors.LAVEL_4} className="mr-[4px]" />
                            <DemiLightText fontSize={13} lineHeight={20} color="#8E9AA0">
                                인증 후 30분간 유지됩니다
                            </DemiLightText>
                        </Row>
                        <DemiLightText fontSize={13} lineHeight={20} color="#8E9AA0" className="mb-[20px]">
                            바로 잠그려면 상단의 잠금 버튼을 눌러주세요
                        </DemiLightText>
                    </>
                )}

                <Row className="self-end">
                    <Col
                        className="w-[91px] h-[41px] bg-[#F2F4F6] rounded-[11px] cursor-pointer mr-2 flex justify-center items-center"
                        onClick={onCancel}
                    >
                        <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                            취소
                        </RegularText>
                    </Col>
                    <Col
                        className="w-[91px] h-[41px] bg-primary rounded-[11px] cursor-pointer flex justify-center items-center"
                        onClick={handleOk}
                    >
                        <RegularText fontSize={16} lineHeight={23} color="white">
                            확인
                        </RegularText>
                    </Col>
                </Row>
            </Col>
        </Col>
    );
};

export default PayrollAuthModal;

type TextInputProps = {
    value: string | number;
    onChange: (value: string | number) => void;
    unit?: string; // 단위 (예: %, 원)
    placeholder?: string; // 플레이스홀더 텍스트
    width?: number; // 입력 필드 너비
    disabled?: boolean; // 비활성화 여부
    onBlur?: () => void;
    height?: number;
    className?: string;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    inputRef?: React.RefObject<HTMLInputElement>;
};

const TextInput: React.FC<TextInputProps> = ({
    value,
    onChange,
    unit = '',
    placeholder = '',
    width = 116,
    disabled = false,
    onBlur,
    height = 38,
    className,
    onKeyDown,
    inputRef,
}) => {
    const internalRef = React.useRef<HTMLInputElement>(null);
    const refToUse = inputRef || internalRef;
    // 포커스
    const [isFocused, setIsFocused] = React.useState(false);
    //

    return (
        <div
            className={`flex items-center border  border-solid rounded-[10px] bg-white px-[18px] cursor-text hover:border-primary transition-colors duration-300 ease-in-out ${
                disabled ? 'bg-gray-100 text-gray-400' : 'bg-white'
            } ${isFocused ? 'border-primary' : 'border-[#D9D9D9B2]'} ${className}`}
            onClick={() => {
                refToUse.current?.focus();
            }}
            style={{
                height,
            }}
        >
            <input
                ref={inputRef}
                type="password"
                value={value}
                onChange={(e) => {
                    onChange(e.target.value.replace(/[^0-9]/g, ''));
                }}
                placeholder={placeholder}
                className={`flex flex-grow outline-none w-[80px] text-[16px] leading-[23px] text-[#3D4244]  ${
                    disabled ? 'cursor-not-allowed' : ''
                }`}
                // onFocus={() => setIsFocused(true)}
                // onBlur={() => {
                //     // onChange(text);
                //     setIsFocused(false);
                // }}
                onBlur={onBlur}
                disabled={disabled}
                maxLength={20}
                onKeyDown={onKeyDown}

                // 숫자만
            />
            {unit && <DemiLightText className="flex">{unit}</DemiLightText>}
        </div>
    );
};
